import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API_BASE } from '../utils/apiBase';

const API = API_BASE;
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_key';

const Checkout = () => {
  const { cart, sessionId, clearCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const couponCode = location.state?.couponCode;

  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    delivery_address: '',
    payment_method: 'razorpay'
  });
  const [processing, setProcessing] = useState(false);

  const subtotal = cart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const deliveryCharge = 100;
  const discount = 0;
  const total = subtotal + deliveryCharge - discount;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Dynamically load Razorpay SDK
  const loadRazorpaySDK = () => {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.Razorpay) {
        resolve();
        return;
      }

      // Prevent duplicate injection
      const existingScript = document.querySelector('script[src*="checkout.razorpay.com"]');
      if (existingScript) {
        existingScript.onload = () => resolve();
        return;
      }

      // Inject Razorpay SDK
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Razorpay SDK'));
      document.body.appendChild(script);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);

    try {
      // Step 1: Create Razorpay order
      const orderResponse = await axios.post(`${API}/create-order`, {
        amount: total,
        cart_data: {
          items: cart.items
        },
        customer_data: {
          name: formData.customer_name,
          email: formData.customer_email,
          phone: formData.customer_phone,
          address: formData.delivery_address
        },
        checkout_meta: {
          delivery_charge: deliveryCharge,
          discount: discount,
          coupon_code: couponCode
        }
      });

      const razorpayOrder = orderResponse.data;

      // Step 2: Load Razorpay SDK
      await loadRazorpaySDK();

      // Step 3: Open Razorpay Checkout
      const options = {
        key: RAZORPAY_KEY_ID,
        amount: razorpayOrder.amount,
        currency: 'INR',
        name: 'Homeo Care',
        description: 'Order Payment',
        order_id: razorpayOrder.id,
        handler: async function (response) {
          try {
            // Step 4: Verify payment
            const verifyResponse = await axios.post(`${API}/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            if (verifyResponse.data.success) {
              // Step 5: Continue with existing success flow
              await clearCart();
              toast.success('Payment successful!');
              navigate(`/order-success/${verifyResponse.data.order_id}`);
            }
          } catch (error) {
            console.error('Payment verification failed:', error);
            toast.error('Payment verification failed. Please contact support.');
            setProcessing(false);
          }
        },
        prefill: {
          name: formData.customer_name,
          email: formData.customer_email,
          contact: formData.customer_phone
        },
        theme: {
          color: '#C29D4A'
        },
        modal: {
          ondismiss: function() {
            toast.error('Payment cancelled');
            setProcessing(false);
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to initiate payment. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream py-8">
      <div className="container mx-auto px-4 max-w-2xl">
        <h1 className="text-4xl font-bold text-dark-text mb-8" data-testid="checkout-heading">
          Checkout
        </h1>

        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-dark-text mb-4">Order Summary</h2>
          <div className="space-y-2 mb-4">
            {cart.items.map((item) => (
              <div key={item.id} className="flex justify-between text-gray-700">
                <span>{item.name} x {item.quantity}</span>
                <span>₹{(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="border-t pt-4 space-y-2">
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span>₹{subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Delivery:</span>
              <span>₹{deliveryCharge.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-xl font-bold">
              <span>Total:</span>
              <span>₹{total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold text-dark-text mb-6">Delivery Details</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-dark-text mb-2">Name *</label>
              <input
                type="text"
                name="customer_name"
                value={formData.customer_name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golden focus:border-golden"
                data-testid="customer-name"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-dark-text mb-2">Email *</label>
              <input
                type="email"
                name="customer_email"
                value={formData.customer_email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golden focus:border-golden"
                data-testid="customer-email"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-dark-text mb-2">Phone *</label>
              <input
                type="tel"
                name="customer_phone"
                value={formData.customer_phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golden focus:border-golden"
                data-testid="customer-phone"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-dark-text mb-2">Delivery Address *</label>
              <textarea
                name="delivery_address"
                value={formData.delivery_address}
                onChange={handleChange}
                required
                rows="4"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golden focus:border-golden"
                data-testid="delivery-address"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-dark-text mb-2">Payment Method</label>
              <select
                name="payment_method"
                value={formData.payment_method}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golden focus:border-golden"
                data-testid="payment-method"
              >
                <option value="razorpay">Razorpay (India)</option>
                <option value="stripe">Stripe (International)</option>
              </select>
            </div>

            <button
              type="submit"
              disabled={processing}
              className="w-full btn-primary px-6 py-4 rounded-lg font-semibold text-lg disabled:opacity-50"
              data-testid="place-order-button"
            >
              {processing ? 'Processing...' : 'Place Order'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
